import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CookieDisclaimer from '../components/CookieDisclaimer';
import SEO from '../components/SEO';
import AnimusHeroChrome from '../components/AnimusHeroChrome';
import { apiGet } from '../api/client';
import '../styles.css';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All streamers' },
  { value: 'live', label: 'Live now' },
  { value: 'offline', label: 'Offline' },
];

const SORT_OPTIONS = [
  { value: 'live-first', label: 'Live first' },
  { value: 'name', label: 'Name' },
  { value: 'viewers', label: 'Viewers' },
];

function sortStreamers(list, sort) {
  const sorted = [...list];
  sorted.sort((a, b) => {
    if (sort === 'name') {
      return (a.displayName || a.twitchUsername).localeCompare(b.displayName || b.twitchUsername);
    }
    if (sort === 'viewers') {
      const va = a.isLive ? (a.streamInfo?.viewerCount || 0) : -1;
      const vb = b.isLive ? (b.streamInfo?.viewerCount || 0) : -1;
      return vb - va;
    }
    const la = a.isLive ? 1 : 0;
    const lb = b.isLive ? 1 : 0;
    if (lb !== la) return lb - la;
    return (b.sortOrder || 0) - (a.sortOrder || 0);
  });
  return sorted;
}

function filterStreamers(list, filter) {
  if (filter === 'live') return list.filter((s) => s.isLive);
  if (filter === 'offline') return list.filter((s) => !s.isLive);
  return list;
}

export default function Streamers() {
  const [streamers, setStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('live-first');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStreamers = useCallback(async () => {
    try {
      const data = await apiGet('/streamers');
      setStreamers(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Unable to load streamer data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStreamers();
    const interval = setInterval(fetchStreamers, 60000);
    return () => clearInterval(interval);
  }, [fetchStreamers]);

  const liveCount = useMemo(() => streamers.filter((s) => s.isLive).length, [streamers]);

  const visibleStreamers = useMemo(
    () => sortStreamers(filterStreamers(streamers, filter), sort),
    [streamers, filter, sort],
  );

  const lastUpdatedLabel = lastUpdated
    ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <>
      <SEO
        title="Featured Streamers"
        description="Discover awesome Twitch streamers recommended by ZeMaxi! Find new content creators to watch and support in the gaming community."
        keywords="Twitch streamers, gaming streamers, content creators, Twitch recommendations, gaming community"
        url="https://zemaxi.com/streamers"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="streamers-hero animus-hero animus-hero-bg">
          <AnimusHeroChrome />
          <div className="container">
            <p className="animus-hero-id font-mono">{'// SECTION_STREAMERS'}</p>
            <h1>Featured <span className="highlight-amber">Streamers</span></h1>
            <p>Discover amazing content creators in our community. Live status updates every minute.</p>
          </div>
        </section>

        <section className="streamers-section page-body-section">
          <div className="container">
            <div className="streamers-dashboard">
              <div className="streamers-stats-bar">
                <div className="streamers-stat">
                  <span className="streamers-stat-value">{streamers.length}</span>
                  <span className="streamers-stat-label">Featured</span>
                </div>
                <div className="streamers-stat streamers-stat--live">
                  <span className="streamers-stat-value">{liveCount}</span>
                  <span className="streamers-stat-label">Live now</span>
                </div>
                <div className="streamers-stat">
                  <span className="streamers-stat-value">{lastUpdatedLabel}</span>
                  <span className="streamers-stat-label">Last check</span>
                </div>
              </div>

              <div className="streamers-toolbar">
                <label className="streamers-toolbar-field">
                  <span>Show</span>
                  <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                    {FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <label className="streamers-toolbar-field">
                  <span>Sort</span>
                  <select value={sort} onChange={(e) => setSort(e.target.value)}>
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {loading && (
              <div className="streamers-loading">
                <div className="spinner" />
                <p>Loading streamers…</p>
              </div>
            )}

            {error && !loading && (
              <div className="streamers-error">
                <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                <p>{error}</p>
                <button type="button" className="btn btn-secondary" onClick={fetchStreamers}>Try again</button>
              </div>
            )}

            {!loading && !error && visibleStreamers.length === 0 && (
              <div className="streamers-empty">
                <i className="fab fa-twitch" aria-hidden="true" />
                <p>No streamers match this filter.</p>
              </div>
            )}

            {!loading && !error && visibleStreamers.length > 0 && (
            <ul className="streamers-list-v2">
              {visibleStreamers.map((streamer, index) => {
                const category = streamer.category || 'Variety Streamer';
                const rawDesc = (
                  streamer.description || streamer.twitchDescription || ''
                ).trim();
                const fallbackBio = 'Check out this awesome streamer!';
                const twitchName = streamer.twitchName || streamer.twitchUsername;
                const displayName = streamer.displayName || twitchName;
                const streamTitle =
                  streamer.isLive && streamer.streamInfo?.title?.trim()
                    ? streamer.streamInfo.title.trim()
                    : '';
                const bioText = rawDesc || (!streamer.isLive ? fallbackBio : '');
                const showBio =
                  bioText &&
                  bioText !== streamTitle &&
                  !bioText.startsWith(streamTitle);

                return (
                  <li key={streamer.id} style={{ animationDelay: `${index * 0.04}s` }}>
                    <a
                      href={`https://twitch.tv/${twitchName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`streamer-card-v2 ${streamer.isLive ? 'is-live' : ''}`}
                    >
                      <div className="streamer-card-v2-top">
                        <div className="streamer-card-v2-avatar">
                          {streamer.profilePic ? (
                            <img
                              src={streamer.profilePic}
                              alt=""
                              className="streamer-card-v2-img"
                            />
                          ) : (
                            <span className="streamer-card-v2-img is-placeholder">
                              <i className="fab fa-twitch" aria-hidden="true" />
                            </span>
                          )}
                          {streamer.isLive && (
                            <span className="streamer-card-v2-live">LIVE</span>
                          )}
                        </div>

                        <div className="streamer-card-v2-main">
                          <div className="streamer-card-v2-head">
                            <h3>{displayName}</h3>
                            <span className="streamer-card-v2-category">{category}</span>
                          </div>

                          {streamer.isLive && streamer.streamInfo && (
                            <div className="streamer-card-v2-live-meta">
                              {streamer.streamInfo.gameName && (
                                <span className="streamer-card-v2-game">
                                  <i className="fas fa-gamepad" aria-hidden="true" />
                                  <span className="streamer-card-v2-game-text">
                                    {streamer.streamInfo.gameName}
                                  </span>
                                </span>
                              )}
                              <span className="streamer-card-v2-viewers">
                                <i className="fas fa-eye" aria-hidden="true" />
                                {streamer.streamInfo.viewerCount.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {streamer.isLive && (streamTitle || showBio) && (
                        <div className="streamer-card-v2-details">
                          {streamTitle && (
                            <p
                              className="streamer-card-v2-stream-title"
                              title={streamTitle}
                            >
                              {streamTitle}
                            </p>
                          )}
                          {showBio && (
                            <p className="streamer-card-v2-bio" title={bioText}>
                              {bioText}
                            </p>
                          )}
                        </div>
                      )}

                      {!streamer.isLive && showBio && (
                        <p className="streamer-card-v2-bio streamer-card-v2-bio--offline" title={bioText}>
                          {bioText}
                        </p>
                      )}

                      <div className="streamer-card-v2-action">
                        <span className="streamer-card-v2-btn">
                          <i className="fab fa-twitch" aria-hidden="true" />
                          {streamer.isLive ? 'Watch live' : 'Visit channel'}
                        </span>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
